/**
 * Filter chips for the Review pane. Three groups: confidence,
 * provenance, and decision-kind. Within a group, chips are mutually
 * exclusive (single-select); groups compose with AND.
 *
 * Compact layout — chip groups are inline without label prefixes so
 * the pane fits in a 320px sidebar without wrapping.
 */
import React from 'react';
import { Chip, Stack } from '@mui/material';
import type { DecisionKind } from '../../services/mappingApi';
import { KIND_LABELS } from '../inspector/decisions/components';
import type { ConfidenceBucket } from '../util/colors';

export type ProvenanceFilter = 'ai' | 'manual';

export interface ReviewFilterState {
  confidence: ConfidenceBucket | null;
  provenance: ProvenanceFilter | null;
  decisionKind: DecisionKind | null;
}

export const EMPTY_FILTERS: ReviewFilterState = {
  confidence: null,
  provenance: null,
  decisionKind: null,
};

export interface ReviewFiltersProps {
  state: ReviewFilterState;
  onChange: (next: ReviewFilterState) => void;
  /** Decision-kind chips render only when at least one decision is in scope. */
  showDecisionKindChips: boolean;
  /** Counts driving each chip's badge (optional). */
  decisionKindCounts?: Partial<Record<DecisionKind, number>>;
}

const CHIP_SX = {
  height: 18,
  fontSize: 10,
  '& .MuiChip-label': { px: 0.75 },
} as const;

const KIND_SHORT: Record<DecisionKind, string> = {
  ambiguous_candidates: 'Ambig',
  missing_information: 'Missing',
  schema_policy_conflict: 'Conflict',
  source_data_quality: 'Bad src',
  mapper_repeated_rejection: 'Review',
};

export const ReviewFilters: React.FC<ReviewFiltersProps> = ({
  state,
  onChange,
  showDecisionKindChips,
  decisionKindCounts,
}) => {
  const setConfidence = (c: ConfidenceBucket) =>
    onChange({ ...state, confidence: state.confidence === c ? null : c });
  const setProvenance = (p: ProvenanceFilter) =>
    onChange({ ...state, provenance: state.provenance === p ? null : p });
  const setDecisionKind = (k: DecisionKind) =>
    onChange({ ...state, decisionKind: state.decisionKind === k ? null : k });

  return (
    <Stack
      direction="row"
      sx={{ p: 0.5, flexWrap: 'wrap', gap: 0.25, rowGap: 0.5, alignItems: 'center' }}
    >
      {(['high', 'medium', 'low'] as ConfidenceBucket[]).map((c) => (
        <Chip
          key={c}
          label={c === 'medium' ? 'med' : c}
          size="small"
          variant={state.confidence === c ? 'filled' : 'outlined'}
          color={state.confidence === c ? 'primary' : 'default'}
          onClick={() => setConfidence(c)}
          aria-label={`Filter by ${c} confidence`}
          aria-pressed={state.confidence === c}
          sx={{ ...CHIP_SX, textTransform: 'capitalize' }}
        />
      ))}
      <Chip
        label="AI"
        size="small"
        variant={state.provenance === 'ai' ? 'filled' : 'outlined'}
        color={state.provenance === 'ai' ? 'primary' : 'default'}
        onClick={() => setProvenance('ai')}
        aria-label="Filter to AI-generated"
        aria-pressed={state.provenance === 'ai'}
        sx={CHIP_SX}
      />
      <Chip
        label="Manual"
        size="small"
        variant={state.provenance === 'manual' ? 'filled' : 'outlined'}
        color={state.provenance === 'manual' ? 'primary' : 'default'}
        onClick={() => setProvenance('manual')}
        aria-label="Filter to manual"
        aria-pressed={state.provenance === 'manual'}
        sx={CHIP_SX}
      />
      {showDecisionKindChips &&
        (Object.keys(KIND_LABELS) as DecisionKind[]).map((k) => {
          const count = decisionKindCounts?.[k];
          const short = KIND_SHORT[k] ?? KIND_LABELS[k].label;
          return (
            <Chip
              key={k}
              label={count != null ? `${short} (${count})` : short}
              size="small"
              variant={state.decisionKind === k ? 'filled' : 'outlined'}
              color={state.decisionKind === k ? 'info' : 'default'}
              onClick={() => setDecisionKind(k)}
              aria-label={`Filter by ${KIND_LABELS[k].label} decisions`}
              aria-pressed={state.decisionKind === k}
              sx={CHIP_SX}
            />
          );
        })}
    </Stack>
  );
};

export default ReviewFilters;
